import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';

import { DESCENDING_SORT } from '@app/lib/constants/http.constants';
import { LAST_UPDATED } from '@app/lib/constants/fhir.constant';
import { IHttpParams } from '@app/lib/interfaces';
import { IPaginationOptions } from '@app/lib/pagination/interfaces';
import { IResearchStudy } from 'projects/labcorp/clinical-trials-ui/src/lib/interfaces';
import { PaginationService } from '@app/lib/pagination/services/pagination.service';
import { Router } from '@angular/router';
import { StudiesService } from '../services/studies.service';
import { ToastrService } from 'ngx-toastr';
import * as StudiesActions from './studies.actions';

@Injectable({
  providedIn: 'root'
})
export class StudiesEffects {

  createResearchStudy$ = createEffect(() =>
    this.actions.pipe(
      ofType(StudiesActions.createResearchStudy),
      switchMap(({ researchStudyData, groupData }) => {
        return this.studiesService
                    // Creates Group resource first to make a reference to it in ResearchStudy
                    ._createGroup(groupData)
                    .pipe(
                      switchMap((group: fhir4.Group) => {
                        // Adds created Group ID to ResearchStudy recruitment.eligibility.reference property
                        const newResearchStudy: IResearchStudy = {
                          ...researchStudyData,
                          recruitment: {
                            eligibility: {
                              reference: `Group/${ group.id }`
                            }
                          }
                        };

                        return this.studiesService
                                    // Creates ResearchStudy resource
                                    ._createStudy(newResearchStudy)
                                    .pipe(
                                      map(() => {
                                        this.toastrService.success('Successfully created a new clinical trial');

                                        return StudiesActions.createResearchStudySuccess();
                                      }),
                                      catchError(() => {
                                        this.toastrService.error('An error occured when creating clinical trial. Please try again.');

                                        return of(StudiesActions.createResearchStudyFailed());
                                      })
                                    );
                      }),
                      catchError(() => {
                        this.toastrService.error('An error occured when creating clinical trial. Please try again.');

                        return of(StudiesActions.createResearchStudyFailed());
                      })
                    );
      })
    )
  );

  getResearchStudies$ = createEffect(() =>
    this.actions.pipe(
      ofType(
        StudiesActions.createResearchStudySuccess,
        StudiesActions.getResearchStudies,
        StudiesActions.setPageIndex,
        StudiesActions.updateEditedResearchStudySuccess,
      ),
      withLatestFrom(
        this.studiesService.studyListNavigator$,
        this.paginationService.paginationOptions$
      ),
      switchMap((values) => {
        const studyListNavigator = values[1];
        const paginationOptions: IPaginationOptions = values[2];
        const httpParams: IHttpParams = {
          _count: paginationOptions.perPage,
          _offset: studyListNavigator?.pager && studyListNavigator.pager.allPages?.length ? studyListNavigator.pager.allPages[studyListNavigator.pager.currentPageIndex]?.offset : 0,
          _sort: `${ DESCENDING_SORT }${ LAST_UPDATED }`,
          ...studyListNavigator.filterParams
        };

        if (!studyListNavigator?.reload) {
          this.router.navigateByUrl('/portal/manage-clinical-trials');
        }

        return this.studiesService
                    ._getResearchStudies(httpParams)
                    .pipe(
                      map( ({researchStudies, total}) => {
                        return StudiesActions.getResearchStudiesSuccess({ researchStudies, paginationOptions, total });
                      }),
                      catchError(() => of(StudiesActions.getResearchStudiesFailed()))
                    );
      })
    )
  );

  getTherapeuticAreas$ = createEffect(() =>
    this.actions.pipe(
      ofType(StudiesActions.getTherapeuticAreas),
      withLatestFrom(
        this.studiesService.therapeuticAreas$
      ),
      switchMap((values) => {
        const therapeuticAreas = values[1];

        if (therapeuticAreas) {
          return of(StudiesActions.getTherapeuticAreasSuccess({ therapeuticAreas }));
        }

        return this.studiesService
                    ._getTherapeuticAreas()
                    .pipe(
                      map(result => StudiesActions.getTherapeuticAreasSuccess({ therapeuticAreas: result })),
                      catchError(() => {
                        this.router.navigateByUrl('/unavailable');

                        return of(StudiesActions.getTherapeuticAreasFailed());
                      })
                    );
      })
    )
  );

  markResearchStudyToEdit$ = createEffect(() =>
    this.actions.pipe(
      ofType(StudiesActions.markResearchStudyToEdit),
      switchMap(({ researchStudy }) => {
        return this.studiesService
                    ._getResearchStudyGroup(researchStudy.researchStudy)
                    .pipe(
                      map((group) => {
                        this.router.navigateByUrl('/portal/edit-study');

                        return StudiesActions.markResearchStudyToEditSuccess({ researchStudy: {
                          ...researchStudy,
                          group,
                        } });
                      }),
                      catchError(() => {
                        return of(StudiesActions.markResearchStudyToEditFailed());
                      })
                    );
      })
    )
  );


  updateEditedResearchStudy$ = createEffect(() =>
    this.actions.pipe(
      ofType(StudiesActions.updateEditedResearchStudy),
      switchMap(({ researchStudyData, groupData }) => {
        return this.studiesService
                    ._updateEditedResearchStudyAndGroup(researchStudyData, groupData)
                    .pipe(
                      map(() => {
                        this.toastrService.success('Successfully updated clinical trial');

                        return StudiesActions.updateEditedResearchStudySuccess();
                      }),
                      catchError(() => {
                        this.toastrService.error('An error occured when updating clinical trial. Please try again.');
                        return of(StudiesActions.updateEditedResearchStudyFailed());
                      })
                    );
      })
    )
  );

  updateFilterParams$ = createEffect(() =>
    this.actions.pipe(
      ofType(StudiesActions.updateFilterParams),
      switchMap(() => {
        return of(StudiesActions.setPageIndex({ index: 0 }));
      })
    )
  );

  constructor(
    private readonly actions: Actions,
    private readonly paginationService: PaginationService,
    private readonly router: Router,
    private readonly studiesService: StudiesService,
    private readonly toastrService: ToastrService,
  ) {}
}
